import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { connect } from 'react-redux'
import CurrencyTypeView from '../../../../../itrust_common/components/others/CurrencyTypeView'
import {
  orderWarrantiesLoad,
  orderWarrantyEyeglassesLoad,
  orderWarrantyApply,
  orderWarrantiesReset,
} from '../../../../../redux/actions/order/orderWarrantyActions'
import useInfiniteScroll, { hasMorePages } from './useInfiniteScroll'
import './WarrantyModal.scss'

const RPP = 10

function AddWarrantyModal({
  isOpen,
  onClose,
  order = {},
  preselectedEyeglassId = null,
  warranties = [],
  eyeglasses = [],
  meta = {},
  loading = false,
  eyeglassesLoading = false,
  saving = false,
  orderWarrantiesLoad: loadWarranties,
  orderWarrantyEyeglassesLoad: loadEyeglasses,
  orderWarrantyApply: applyWarranty,
  orderWarrantiesReset: resetWarranties,
}) {
  const [selectedWarrantyId, setSelectedWarrantyId] = useState(null)
  const [selectedEyeglassIds, setSelectedEyeglassIds] = useState([])
  const [page, setPage] = useState(1)
  const leftRef = useRef(null)
  const hasMore = hasMorePages(meta, warranties.length)

  useEffect(() => {
    if (!isOpen) return
    setSelectedWarrantyId(null)
    setSelectedEyeglassIds(preselectedEyeglassId ? [preselectedEyeglassId] : [])
    setPage(1)
    loadWarranties({ page: 1, rpp: RPP })
    return () => resetWarranties()
  }, [isOpen, preselectedEyeglassId])

  useEffect(() => {
    if (!isOpen || !warranties.length || selectedWarrantyId) return
    setSelectedWarrantyId(warranties[0].id)
  }, [isOpen, warranties])

  useEffect(() => {
    if (!isOpen || !order?.id || !selectedWarrantyId) return
    setSelectedEyeglassIds(preselectedEyeglassId ? [preselectedEyeglassId] : [])
    loadEyeglasses(order.id, selectedWarrantyId)
  }, [isOpen, order?.id, selectedWarrantyId, preselectedEyeglassId])

  useEffect(() => {
    if (!isOpen || !preselectedEyeglassId || !eyeglasses.length) return
    if (eyeglasses.some((item) => item.id === preselectedEyeglassId)) {
      setSelectedEyeglassIds([preselectedEyeglassId])
    }
  }, [isOpen, preselectedEyeglassId, eyeglasses])

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    const nextPage = page + 1
    setPage(nextPage)
    loadWarranties({ page: nextPage, rpp: RPP })
  }, [loading, hasMore, page, loadWarranties])

  const sentinelRef = useInfiniteScroll({
    rootRef: leftRef,
    hasMore: isOpen && hasMore,
    loading,
    onLoadMore: loadMore,
  })

  const selectedWarranty = warranties.find((w) => w.id === selectedWarrantyId)

  const toggleEyeglass = (id) => {
    setSelectedEyeglassIds((ids) => (
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    ))
  }

  const handleAdd = () => {
    if (!order?.id || !selectedWarranty || !selectedEyeglassIds.length) return

    applyWarranty(order.id, {
      order: {
        warranties_attributes: selectedEyeglassIds.map((eyeglassId) => ({
          orders_order_id: order.id,
          orders_eyeglass_id: eyeglassId,
          settings_warranty_id: selectedWarranty.id,
        })),
      },
    })
      .then(() => onClose())
      .catch(() => {})
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="warranty-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose() }}
    >
      <div className="warranty-modal">
        <div className="warranty-modal-head">
          <div>
            <h4 className="warranty-modal-title">Add Warranty</h4>
            <div className="warranty-modal-sub">Select a warranty, then choose eyeglasses to cover.</div>
          </div>
          <button type="button" className="close-x" onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="warranty-modal-body">
          <div className="warranty-panel warranty-panel-left" ref={leftRef}>
            <div className="modal-section-label">Warranty Programs</div>

            {loading && !warranties.length ? (
              <div className="text-center py-5 text-muted">
                <i className="la la-spinner la-spin la-2x text-primary" />
              </div>
            ) : !warranties.length ? (
              <div className="text-center py-5 text-muted">No active warranties.</div>
            ) : (
              <>
                {warranties.map((warranty) => {
                  const selected = warranty.id === selectedWarrantyId
                  const term = warranty.term_value != null && warranty.term_unit
                    ? `${warranty.term_value} ${warranty.term_unit}`
                    : null
                  const reasons = warranty.reasons || warranty.reasons_attributes || []

                  return (
                    <div
                      key={warranty.id}
                      className={`warranty-card ${selected ? 'selected' : ''}`}
                      onClick={() => setSelectedWarrantyId(warranty.id)}
                    >
                      <span className="radio" />
                      <div className="warranty-card-content">
                        <div className="d-flex justify-content-between align-items-start">
                          {warranty.name && <span className="warranty-name">{warranty.name}</span>}
                          {warranty.price != null && (
                            <span className="warranty-price">
                              <CurrencyTypeView amount={warranty.price} />
                            </span>
                          )}
                        </div>
                        {(term || warranty.applies_to) && (
                          <div className="warranty-meta-line">
                            {term}
                            {term && warranty.applies_to && <span className="use-dot">·</span>}
                            {warranty.applies_to && <>Applies to {warranty.applies_to}</>}
                          </div>
                        )}
                        {warranty.summary && <div className="warranty-summary">{warranty.summary}</div>}
                        {reasons.length > 0 && (
                          <div className="warranty-reasons">
                            {reasons.map((reason) => (
                              <div key={reason.id || reason.name} className="warranty-reason-row">
                                <span className="warranty-reason-name">{reason.name}</span>
                                {reason.usage_limit != null && (
                                  <span className="warranty-reason-limit">
                                    {reason.usage_limit} {Number(reason.usage_limit) === 1 ? 'use' : 'uses'}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}

                {loading && warranties.length > 0 && (
                  <div className="text-center py-3 text-muted">
                    <i className="la la-spinner la-spin text-primary" />
                  </div>
                )}
                {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
              </>
            )}
          </div>

          <div className="warranty-panel">
            <div className="modal-section-label">Eligible Eyeglasses</div>

            {eyeglassesLoading ? (
              <div className="text-center py-5 text-muted">
                <i className="la la-spinner la-spin la-2x text-primary" />
              </div>
            ) : !eyeglasses.length ? (
              <div className="text-center py-5 text-muted">No matching eyeglasses.</div>
            ) : (
              eyeglasses.map((eyeglass) => {
                const selected = selectedEyeglassIds.includes(eyeglass.id)
                const appliesTo = String(selectedWarranty?.applies_to || 'both').toLowerCase()
                const showFrame = appliesTo === 'frame' || appliesTo === 'both'
                const showLens = appliesTo === 'lens' || appliesTo === 'both'
                const frame = eyeglass.frame_details
                  ? [eyeglass.frame_details.brand, eyeglass.frame_details.model, eyeglass.frame_details.color]
                      .filter(Boolean).join(' · ')
                  : null
                const lensType = [...new Set((eyeglass.lens_type_names || []).filter(Boolean))].join(' · ')
                const addons = [...new Set((eyeglass.addons_names || []).filter(Boolean))]
                return (
                  <div
                    key={eyeglass.id}
                    className={`eyeglass-card ${selected ? 'selected' : ''}`}
                    onClick={() => toggleEyeglass(eyeglass.id)}
                  >
                    <span className="check" />
                    <div className="eyeglass-card-content">
                      <div className="d-flex justify-content-between align-items-start">
                        <span className="eyeglass-title text-truncate">
                          {eyeglass.sub_order_id || `Eyeglass #${eyeglass.id}`}
                        </span>
                        {eyeglass.total != null && Number(eyeglass.total) !== 0 && (
                          <span className="eyeglass-total">
                            <CurrencyTypeView amount={eyeglass.total} />
                          </span>
                        )}
                      </div>
                      {(showFrame && frame) || showLens ? (
                        <div className="eyeglass-details">
                          {showFrame && frame && (
                            <div className="eyeglass-detail">
                              <span className="label">Frame</span>
                              <span className="value">{frame}</span>
                            </div>
                          )}
                          {showLens && lensType && (
                            <div className="eyeglass-detail">
                              <span className="label">Lens Type</span>
                              <span className="value">{lensType}</span>
                            </div>
                          )}
                          {showLens && eyeglass.lens_material_name && (
                            <div className="eyeglass-detail">
                              <span className="label">Lens Material</span>
                              <span className="value">{eyeglass.lens_material_name}</span>
                            </div>
                          )}
                          {showLens && addons.length > 0 && (
                            <div className="eyeglass-detail eyeglass-detail-addons">
                              <span className="label">Addons</span>
                              <div className="eyeglass-tags">
                                {addons.map((addon) => (
                                  <span key={addon} className="eyeglass-tag">{addon}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div className="warranty-modal-foot">
          <div className="foot-note">
            {selectedEyeglassIds.length
              ? `${selectedEyeglassIds.length} selected`
              : 'Select at least one eyeglass'}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={!selectedWarranty || !selectedEyeglassIds.length || saving}
            >
              {saving ? 'Adding...' : 'Add Warranty'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

const mapStateToProps = (state) => {
  const s = state.orderWarranty || {}
  return {
    warranties: s.warranties || [],
    eyeglasses: s.eyeglasses || [],
    meta: s.warrantiesMeta || {},
    loading: Boolean(s.loading),
    eyeglassesLoading: Boolean(s.eyeglassesLoading),
    saving: Boolean(s.saving),
  }
}

export default connect(mapStateToProps, {
  orderWarrantiesLoad,
  orderWarrantyEyeglassesLoad,
  orderWarrantyApply,
  orderWarrantiesReset,
})(AddWarrantyModal)
